import { useState, useEffect, useMemo } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import {
    ArrowLeft,
    Edit2,
    X,
    Save,
    Trash2,
    AlertTriangle
} from 'lucide-react';
import { useEquipment, useUpdateEquipment, useDeactivateEquipment } from '@/hooks/useEquipment';
import {
    type Equipment,
    type EquipmentStatus,
    EQUIPMENT_CATEGORIES
} from '@/types/equipment';
import {
    getAllowedTransitions,
    getTransition
} from '@/types/equipmentStatus';
import { useAuth } from '@/lib/auth';
import PtLogo from '@/components/PtLogo';
import EquipmentStatusPill from '@/components/equipment/EquipmentStatusPill';
import FieldGroup from '@/components/equipment/FieldGroup';
import FieldRow, {
    EmptyValue,
    MonoValue,
    formatCurrency,
    formatDate,
    formatDateTime,
    formatBoolean
} from '@/components/equipment/FieldRow';
import {
    TextInput,
    TextareaInput,
    NumberInput,
    DateInput,
    SelectInput,
    CheckboxInput
} from '@/components/equipment/FieldEditors';

// ============================================================================
// EquipmentDetailRoute
// ----------------------------------------------------------------------------
// Read mode by default; "Edit" button toggles edit mode for the entire record.
// On save, mutations write through useUpdateEquipment; the trigger on the
// underlying table sets updated_by + updated_at automatically.
//
// Status field gets special treatment: showing only valid transitions per the
// state machine, and prompting for a reason when the transition requires one.
// Maintenance completion (any status → 'available' from 'maintenance') auto-
// computes next_maintenance_due from maintenance_interval_days.
// ============================================================================

type DraftPatch = Partial<Equipment>;

export default function EquipmentDetailRoute() {
    const { id: idParam } = useParams<{ id: string }>();
    const id = idParam ? parseInt(idParam, 10) : null;
    const navigate = useNavigate();
    const { signOut, user } = useAuth();

    const { data: eq, isLoading, error, refetch } = useEquipment(id);
    const updateMut = useUpdateEquipment();
    const deactivateMut = useDeactivateEquipment();

    const [editing, setEditing] = useState(false);
    const [draft, setDraft] = useState<DraftPatch>({});
    const [reasonForStatusChange, setReasonForStatusChange] = useState('');
    const [validationError, setValidationError] = useState<string | null>(null);
    const [showRetireConfirm, setShowRetireConfirm] = useState(false);

    // When equipment loads or we exit edit mode, reset the draft
    useEffect(() => {
        if (!editing && eq) {
            setDraft({});
            setReasonForStatusChange('');
            setValidationError(null);
        }
    }, [editing, eq]);

    // Helper to update a draft field. Stores delta only — fields not touched
    // stay un-set in the draft and are unchanged on save.
    const setDraftField = <K extends keyof Equipment>(
        key: K,
        value: Equipment[K]
    ) => {
        setDraft((prev) => ({ ...prev, [key]: value }));
        setValidationError(null);
    };

    // Resolved value for a field — draft if set, otherwise the loaded equipment
    const f = <K extends keyof Equipment>(key: K): Equipment[K] | undefined => {
        if (key in draft) return draft[key] as Equipment[K];
        return eq?.[key];
    };

    // What status transition would happen on save?
    const pendingStatusChange = useMemo(() => {
        if (!eq) return null;
        const newStatus = draft.status;
        if (!newStatus || newStatus === eq.status) return null;
        return getTransition(
            eq.status,
            newStatus as EquipmentStatus,
            eq.sterilization_required
        );
    }, [draft.status, eq]);

    const allowedStatusOptions = useMemo(() => {
        if (!eq) return [];
        const transitions = getAllowedTransitions(eq.status, eq.sterilization_required);
        return [
            { value: eq.status, label: `Keep as: ${eq.status.replace(/-/g, ' ')}` },
            ...transitions.map((t) => ({ value: t.to, label: t.label }))
        ];
    }, [eq]);

    async function handleSave() {
        if (!eq) return;
        setValidationError(null);

        const patch: DraftPatch = { ...draft };

        // Status transition validation + side effects
        if (pendingStatusChange) {
            if (pendingStatusChange.requires_reason && !reasonForStatusChange.trim()) {
                setValidationError(
                    `A reason is required when ${pendingStatusChange.label.toLowerCase()}.`
                );
                return;
            }

            // Auto-compute next maintenance date if completing maintenance
            if (eq.status === 'maintenance' && pendingStatusChange.to === 'available') {
                const today = new Date();
                patch.last_maintenance_date = today.toISOString().slice(0, 10);
                if (eq.maintenance_interval_days) {
                    const next = new Date(today);
                    next.setDate(next.getDate() + eq.maintenance_interval_days);
                    patch.next_maintenance_due = next.toISOString().slice(0, 10);
                }
            }

            // Out-of-service: write the reason; clear it when leaving out-of-service
            if (pendingStatusChange.to === 'out-of-service') {
                patch.out_of_service = true;
                patch.out_of_service_reason = reasonForStatusChange.trim();
            } else if (eq.status === 'out-of-service') {
                patch.out_of_service = false;
                patch.out_of_service_reason = null;
            }
        }

        // Required-field guards on whatever the user typed
        if (patch.equipment_name !== undefined && !patch.equipment_name?.trim()) {
            setValidationError('Equipment name cannot be blank.');
            return;
        }
        if (patch.equipment_category !== undefined && !patch.equipment_category?.trim()) {
            setValidationError('Equipment category cannot be blank.');
            return;
        }
        if (patch.minimum_quantity != null && patch.minimum_quantity < 0) {
            setValidationError('Minimum quantity cannot be negative.');
            return;
        }
        if (patch.quantity_on_hand != null && patch.quantity_on_hand < 0) {
            setValidationError('Quantity on hand cannot be negative.');
            return;
        }

        try {
            await updateMut.mutateAsync({ id: eq.equipment_id, patch });
            setEditing(false);
        } catch (err) {
            setValidationError(
                err instanceof Error ? err.message : 'Save failed. Please try again.'
            );
        }
    }

    async function handleRetire() {
        if (!eq) return;
        try {
            await deactivateMut.mutateAsync(eq.equipment_id);
            navigate('/equipment', { replace: true });
        } catch (err) {
            setValidationError(
                err instanceof Error ? err.message : 'Could not retire item.'
            );
        }
    }

    function handleCancel() {
        setEditing(false);
        setDraft({});
        setReasonForStatusChange('');
        setValidationError(null);
    }

    // ------------------------------------------------------------------------
    // Render
    // ------------------------------------------------------------------------

    if (isLoading) {
        return (
            <div className="min-h-full flex items-center justify-center">
                <span className="pt-spinner mr-3" />
                <span className="text-pt-text-muted">Loading equipment…</span>
            </div>
        );
    }

    if (error || !eq) {
        return (
            <div className="min-h-full flex items-center justify-center px-4">
                <div className="max-w-md text-center">
                    <h1 className="text-pt-red font-semibold mb-2">
                        Could not load equipment
                    </h1>
                    <p className="text-sm text-pt-text-muted mb-4">
                        {error instanceof Error
                            ? error.message
                            : `No equipment found with ID ${id}.`}
                    </p>
                    <div className="flex gap-3 justify-center">
                        <Link
                            to="/equipment"
                            className="px-4 py-2 border border-pt-input-border rounded-md text-pt-text hover:border-pt-gold"
                        >
                            Back to catalog
                        </Link>
                        <button
                            onClick={() => refetch()}
                            className="px-4 py-2 bg-pt-gold text-pt-darker rounded-md text-sm font-semibold"
                        >
                            Retry
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-full pb-12">
            {/* Header */}
            <header className="border-b border-pt-border/60 sticky top-0 z-10">
                <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between gap-4">
                    <div className="flex items-center gap-3 min-w-0">
                        <Link
                            to="/equipment"
                            className="text-pt-text-muted hover:text-pt-gold transition-colors"
                            title="Back to catalog"
                        >
                            <ArrowLeft size={20} />
                        </Link>
                        <PtLogo size={32} />
                        <div className="min-w-0">
                            <div className="text-base font-display font-semibold tracking-wide leading-tight truncate">
                                {eq.equipment_name}
                            </div>
                            <div className="text-xs text-pt-text-dim leading-tight">
                                #{eq.equipment_id} · {eq.equipment_category}
                            </div>
                        </div>
                    </div>

                    <div className="flex items-center gap-3">
                        {!editing && (
                            <button
                                onClick={() => setEditing(true)}
                                className="inline-flex items-center gap-2 px-3 py-2 bg-pt-gold text-pt-darker text-sm font-semibold uppercase tracking-wide rounded-md hover:shadow-pt-glow-gold transition-shadow"
                            >
                                <Edit2 size={14} strokeWidth={2.5} />
                                Edit
                            </button>
                        )}
                        {editing && (
                            <>
                                <button
                                    onClick={handleCancel}
                                    disabled={updateMut.isPending}
                                    className="inline-flex items-center gap-2 px-3 py-2 border border-pt-input-border rounded-md text-pt-text-muted hover:border-pt-gold hover:text-pt-text disabled:opacity-50"
                                >
                                    <X size={14} />
                                    Cancel
                                </button>
                                <button
                                    onClick={handleSave}
                                    disabled={updateMut.isPending}
                                    className="inline-flex items-center gap-2 px-3 py-2 bg-pt-gold text-pt-darker text-sm font-semibold uppercase tracking-wide rounded-md disabled:opacity-50"
                                >
                                    {updateMut.isPending ? (
                                        <span className="pt-spinner inline-block" />
                                    ) : (
                                        <Save size={14} strokeWidth={2.5} />
                                    )}
                                    Save
                                </button>
                            </>
                        )}
                        <span className="text-xs text-pt-text-muted hidden sm:inline">
                            {user?.email}
                        </span>
                        <button
                            onClick={signOut}
                            className="text-xs uppercase tracking-wide text-pt-text-muted hover:text-pt-gold"
                        >
                            Sign out
                        </button>
                    </div>
                </div>

                {/* Edit-mode subheader: validation error or pending status note */}
                {editing && (validationError || pendingStatusChange) && (
                    <div className="border-t border-pt-border bg-pt-dark">
                        <div className="max-w-4xl mx-auto px-6 py-2">
                            {validationError && (
                                <div className="text-pt-red text-sm flex items-center gap-2">
                                    <AlertTriangle size={14} />
                                    {validationError}
                                </div>
                            )}
                            {!validationError && pendingStatusChange?.side_effect && (
                                <div className="text-pt-amber text-xs flex items-center gap-2">
                                    <AlertTriangle size={14} />
                                    {pendingStatusChange.side_effect}
                                </div>
                            )}
                        </div>
                    </div>
                )}
            </header>

            <main className="max-w-4xl mx-auto px-6 py-6 space-y-4">
                {/* Status banner — always visible at top */}
                <section className="bg-pt-panel border border-pt-border rounded-lg p-4 flex items-center justify-between gap-4">
                    <div className="flex items-center gap-3">
                        <EquipmentStatusPill status={eq.status} />
                        {eq.out_of_service && eq.out_of_service_reason && (
                            <span className="text-xs text-pt-text-muted">
                                — {eq.out_of_service_reason}
                            </span>
                        )}
                    </div>
                    <div className="text-xs text-pt-text-dim text-right">
                        Updated {formatDateTime(eq.updated_at)}
                    </div>
                </section>

                {/* Identity */}
                <FieldGroup title="Identity" description="Name, category, and identifying numbers">
                    <FieldRow label="Equipment Name">
                        {editing ? (
                            <TextInput
                                value={f('equipment_name') ?? null}
                                onChange={(v) => setDraftField('equipment_name', v ?? '')}
                            />
                        ) : (
                            eq.equipment_name
                        )}
                    </FieldRow>
                    <FieldRow label="Category">
                        {editing ? (
                            <SelectInput
                                value={f('equipment_category') ?? null}
                                onChange={(v) => setDraftField('equipment_category', v ?? '')}
                                options={EQUIPMENT_CATEGORIES.map((c) => ({
                                    value: c,
                                    label: c
                                }))}
                            />
                        ) : (
                            eq.equipment_category
                        )}
                    </FieldRow>
                    <FieldRow label="Subcategory">
                        {editing ? (
                            <TextInput
                                value={f('equipment_subcategory') ?? null}
                                onChange={(v) => setDraftField('equipment_subcategory', v)}
                            />
                        ) : (
                            eq.equipment_subcategory ?? <EmptyValue />
                        )}
                    </FieldRow>
                    <FieldRow label="Manufacturer">
                        {editing ? (
                            <TextInput
                                value={f('manufacturer') ?? null}
                                onChange={(v) => setDraftField('manufacturer', v)}
                            />
                        ) : (
                            eq.manufacturer ?? <EmptyValue />
                        )}
                    </FieldRow>
                    <FieldRow label="Model #">
                        {editing ? (
                            <TextInput
                                value={f('model_number') ?? null}
                                onChange={(v) => setDraftField('model_number', v)}
                            />
                        ) : eq.model_number ? (
                            <MonoValue>{eq.model_number}</MonoValue>
                        ) : (
                            <EmptyValue />
                        )}
                    </FieldRow>
                    <FieldRow label="Serial #">
                        {editing ? (
                            <TextInput
                                value={f('serial_number') ?? null}
                                onChange={(v) => setDraftField('serial_number', v)}
                            />
                        ) : eq.serial_number ? (
                            <MonoValue>{eq.serial_number}</MonoValue>
                        ) : (
                            <EmptyValue />
                        )}
                    </FieldRow>
                    <FieldRow label="Asset Tag">
                        {editing ? (
                            <TextInput
                                value={f('asset_tag') ?? null}
                                onChange={(v) => setDraftField('asset_tag', v)}
                            />
                        ) : eq.asset_tag ? (
                            <MonoValue>{eq.asset_tag}</MonoValue>
                        ) : (
                            <EmptyValue />
                        )}
                    </FieldRow>
                    <FieldRow label="Description" fullWidth>
                        {editing ? (
                            <TextareaInput
                                value={f('description') ?? null}
                                onChange={(v) => setDraftField('description', v)}
                                rows={2}
                            />
                        ) : (
                            eq.description ?? <EmptyValue />
                        )}
                    </FieldRow>
                </FieldGroup>

                {/* Inventory */}
                <FieldGroup title="Inventory" description="Quantities and physical location">
                    <FieldRow label="On Hand">
                        {editing ? (
                            <NumberInput
                                value={f('quantity_on_hand') ?? null}
                                onChange={(v) => setDraftField('quantity_on_hand', v ?? 0)}
                                min={0}
                            />
                        ) : (
                            <span className="font-mono">{eq.quantity_on_hand}</span>
                        )}
                    </FieldRow>
                    <FieldRow label="In Use">
                        {editing ? (
                            <NumberInput
                                value={f('quantity_in_use') ?? null}
                                onChange={(v) => setDraftField('quantity_in_use', v ?? 0)}
                                min={0}
                            />
                        ) : (
                            <span className="font-mono">{eq.quantity_in_use}</span>
                        )}
                    </FieldRow>
                    <FieldRow label="In Sterilization">
                        {editing ? (
                            <NumberInput
                                value={f('quantity_in_sterilization') ?? null}
                                onChange={(v) => setDraftField('quantity_in_sterilization', v ?? 0)}
                                min={0}
                            />
                        ) : (
                            <span className="font-mono">{eq.quantity_in_sterilization}</span>
                        )}
                    </FieldRow>
                    <FieldRow label="Minimum (Par)">
                        {editing ? (
                            <NumberInput
                                value={f('minimum_quantity') ?? null}
                                onChange={(v) => setDraftField('minimum_quantity', v ?? 0)}
                                min={0}
                            />
                        ) : (
                            <span className="font-mono">{eq.minimum_quantity}</span>
                        )}
                    </FieldRow>
                    <FieldRow label="Maximum">
                        {editing ? (
                            <NumberInput
                                value={f('maximum_quantity') ?? null}
                                onChange={(v) => setDraftField('maximum_quantity', v)}
                                min={0}
                            />
                        ) : eq.maximum_quantity != null ? (
                            <span className="font-mono">{eq.maximum_quantity}</span>
                        ) : (
                            <EmptyValue />
                        )}
                    </FieldRow>
                    <FieldRow label="Primary Storage">
                        {editing ? (
                            <TextInput
                                value={f('primary_storage_location') ?? null}
                                onChange={(v) => setDraftField('primary_storage_location', v)}
                            />
                        ) : (
                            eq.primary_storage_location ?? <EmptyValue />
                        )}
                    </FieldRow>
                    <FieldRow label="Current Location">
                        {editing ? (
                            <TextInput
                                value={f('current_location') ?? null}
                                onChange={(v) => setDraftField('current_location', v)}
                            />
                        ) : (
                            eq.current_location ?? <EmptyValue />
                        )}
                    </FieldRow>
                </FieldGroup>

                {/* Sterilization */}
                <FieldGroup
                    title="Sterilization"
                    description="Reusability and cleaning workflow"
                    defaultOpen={eq.reusable || !!eq.sterilization_required}
                >
                    <FieldRow label="Reusable">
                        {editing ? (
                            <CheckboxInput
                                value={f('reusable') ?? null}
                                onChange={(v) => setDraftField('reusable', v)}
                                label="This item is reusable"
                            />
                        ) : (
                            formatBoolean(eq.reusable)
                        )}
                    </FieldRow>
                    <FieldRow label="Sterilization Required">
                        {editing ? (
                            <CheckboxInput
                                value={f('sterilization_required') ?? null}
                                onChange={(v) => setDraftField('sterilization_required', v)}
                                label="Requires sterilization between uses"
                            />
                        ) : (
                            formatBoolean(eq.sterilization_required)
                        )}
                    </FieldRow>
                    <FieldRow label="Method">
                        {editing ? (
                            <TextInput
                                value={f('sterilization_method') ?? null}
                                onChange={(v) => setDraftField('sterilization_method', v)}
                                placeholder="e.g. Steam Autoclave, STERIS, Surface Wipe"
                            />
                        ) : (
                            eq.sterilization_method ?? <EmptyValue />
                        )}
                    </FieldRow>
                </FieldGroup>

                {/* Maintenance */}
                <FieldGroup
                    title="Maintenance"
                    description="Service intervals and recent maintenance history"
                >
                    <FieldRow label="Requires Maintenance">
                        {editing ? (
                            <CheckboxInput
                                value={f('requires_maintenance') ?? null}
                                onChange={(v) => setDraftField('requires_maintenance', v)}
                                label="Has scheduled maintenance"
                            />
                        ) : (
                            formatBoolean(eq.requires_maintenance)
                        )}
                    </FieldRow>
                    <FieldRow label="Interval">
                        {editing ? (
                            <NumberInput
                                value={f('maintenance_interval_days') ?? null}
                                onChange={(v) => setDraftField('maintenance_interval_days', v)}
                                min={1}
                                suffix="days"
                            />
                        ) : eq.maintenance_interval_days != null ? (
                            `${eq.maintenance_interval_days} days`
                        ) : (
                            <EmptyValue />
                        )}
                    </FieldRow>
                    <FieldRow label="Last Maintenance">
                        {editing ? (
                            <DateInput
                                value={f('last_maintenance_date') ?? null}
                                onChange={(v) => setDraftField('last_maintenance_date', v)}
                            />
                        ) : (
                            formatDate(eq.last_maintenance_date)
                        )}
                    </FieldRow>
                    <FieldRow label="Next Due">
                        {editing ? (
                            <DateInput
                                value={f('next_maintenance_due') ?? null}
                                onChange={(v) => setDraftField('next_maintenance_due', v)}
                            />
                        ) : (
                            <NextMaintenanceDisplay date={eq.next_maintenance_due} />
                        )}
                    </FieldRow>
                    <FieldRow label="Notes" fullWidth>
                        {editing ? (
                            <TextareaInput
                                value={f('maintenance_notes') ?? null}
                                onChange={(v) => setDraftField('maintenance_notes', v)}
                                rows={2}
                            />
                        ) : (
                            eq.maintenance_notes ?? <EmptyValue />
                        )}
                    </FieldRow>
                </FieldGroup>

                {/* Compliance */}
                <FieldGroup title="Compliance" description="FDA approval and inspection schedule">
                    <FieldRow label="FDA Approved">
                        {editing ? (
                            <CheckboxInput
                                value={f('fda_approved') ?? null}
                                onChange={(v) => setDraftField('fda_approved', v)}
                                label="FDA-approved device"
                            />
                        ) : (
                            formatBoolean(eq.fda_approved)
                        )}
                    </FieldRow>
                    <FieldRow label="FDA Clearance #">
                        {editing ? (
                            <TextInput
                                value={f('fda_clearance_number') ?? null}
                                onChange={(v) => setDraftField('fda_clearance_number', v)}
                                placeholder="e.g. K123456"
                            />
                        ) : eq.fda_clearance_number ? (
                            <MonoValue>{eq.fda_clearance_number}</MonoValue>
                        ) : (
                            <EmptyValue />
                        )}
                    </FieldRow>
                    <FieldRow label="Requires Inspection">
                        {editing ? (
                            <CheckboxInput
                                value={f('requires_inspection') ?? null}
                                onChange={(v) => setDraftField('requires_inspection', v)}
                                label="Has scheduled regulatory inspection"
                            />
                        ) : (
                            formatBoolean(eq.requires_inspection)
                        )}
                    </FieldRow>
                    <FieldRow label="Last Inspection">
                        {editing ? (
                            <DateInput
                                value={f('last_inspection_date') ?? null}
                                onChange={(v) => setDraftField('last_inspection_date', v)}
                            />
                        ) : (
                            formatDate(eq.last_inspection_date)
                        )}
                    </FieldRow>
                    <FieldRow label="Inspection Due">
                        {editing ? (
                            <DateInput
                                value={f('inspection_due_date') ?? null}
                                onChange={(v) => setDraftField('inspection_due_date', v)}
                            />
                        ) : (
                            formatDate(eq.inspection_due_date)
                        )}
                    </FieldRow>
                </FieldGroup>

                {/* Lifecycle */}
                <FieldGroup
                    title="Lifecycle"
                    description="Acquisition and warranty"
                    defaultOpen={false}
                >
                    <FieldRow label="Acquisition Date">
                        {editing ? (
                            <DateInput
                                value={f('acquisition_date') ?? null}
                                onChange={(v) => setDraftField('acquisition_date', v)}
                            />
                        ) : (
                            formatDate(eq.acquisition_date)
                        )}
                    </FieldRow>
                    <FieldRow label="Purchase Price">
                        {editing ? (
                            <NumberInput
                                value={f('purchase_price') ?? null}
                                onChange={(v) => setDraftField('purchase_price', v)}
                                min={0}
                                step="0.01"
                                suffix="USD"
                            />
                        ) : (
                            formatCurrency(eq.purchase_price)
                        )}
                    </FieldRow>
                    <FieldRow label="Vendor">
                        {editing ? (
                            <TextInput
                                value={f('vendor') ?? null}
                                onChange={(v) => setDraftField('vendor', v)}
                            />
                        ) : (
                            eq.vendor ?? <EmptyValue />
                        )}
                    </FieldRow>
                    <FieldRow label="Warranty Expiration">
                        {editing ? (
                            <DateInput
                                value={f('warranty_expiration') ?? null}
                                onChange={(v) => setDraftField('warranty_expiration', v)}
                            />
                        ) : (
                            formatDate(eq.warranty_expiration)
                        )}
                    </FieldRow>
                </FieldGroup>

                {/* Billing */}
                <FieldGroup
                    title="Billing"
                    description="Charge capture for billable equipment usage"
                    defaultOpen={!!eq.billable}
                >
                    <FieldRow label="Billable">
                        {editing ? (
                            <CheckboxInput
                                value={f('billable') ?? null}
                                onChange={(v) => setDraftField('billable', v)}
                                label="Bill patients for usage of this item"
                            />
                        ) : (
                            formatBoolean(eq.billable)
                        )}
                    </FieldRow>
                    <FieldRow label="Billing Code">
                        {editing ? (
                            <TextInput
                                value={f('billing_code') ?? null}
                                onChange={(v) => setDraftField('billing_code', v)}
                                placeholder="CPT or HCPCS"
                            />
                        ) : eq.billing_code ? (
                            <MonoValue>{eq.billing_code}</MonoValue>
                        ) : (
                            <EmptyValue />
                        )}
                    </FieldRow>
                    <FieldRow label="Billing Price">
                        {editing ? (
                            <NumberInput
                                value={f('billing_price') ?? null}
                                onChange={(v) => setDraftField('billing_price', v)}
                                min={0}
                                step="0.01"
                                suffix="USD"
                            />
                        ) : (
                            formatCurrency(eq.billing_price)
                        )}
                    </FieldRow>
                </FieldGroup>

                {/* Status & Out-of-Service */}
                <FieldGroup
                    title="Status"
                    description="Current status and transition controls"
                    accentColor="text-pt-amber"
                >
                    <FieldRow label="Current Status">
                        <EquipmentStatusPill status={eq.status} />
                    </FieldRow>
                    {editing && allowedStatusOptions.length > 1 && (
                        <FieldRow label="Change To">
                            <SelectInput
                                value={(f('status') as string) ?? eq.status}
                                onChange={(v) => setDraftField('status', (v ?? eq.status) as EquipmentStatus)}
                                options={allowedStatusOptions}
                            />
                        </FieldRow>
                    )}
                    {editing && pendingStatusChange?.requires_reason && (
                        <FieldRow label="Reason" fullWidth>
                            <TextareaInput
                                value={reasonForStatusChange || null}
                                onChange={(v) => setReasonForStatusChange(v ?? '')}
                                placeholder="Required: explain why this status change is happening"
                                rows={2}
                                required
                                error={!reasonForStatusChange.trim()}
                            />
                        </FieldRow>
                    )}
                    {!editing && eq.out_of_service_reason && (
                        <FieldRow label="Out-of-Service Reason" fullWidth>
                            {eq.out_of_service_reason}
                        </FieldRow>
                    )}
                </FieldGroup>

                {/* Footer: retire (soft delete) action — only visible in view mode */}
                {!editing && eq.is_active !== false && eq.status !== 'retired' && (
                    <div className="mt-8 pt-6 border-t border-pt-border">
                        {!showRetireConfirm ? (
                            <button
                                onClick={() => setShowRetireConfirm(true)}
                                className="text-xs text-pt-text-dim hover:text-pt-red flex items-center gap-2"
                            >
                                <Trash2 size={12} />
                                Retire this item
                            </button>
                        ) : (
                            <div className="bg-pt-red/5 border border-pt-red/30 rounded-lg p-4 space-y-3">
                                <div className="text-sm text-pt-text">
                                    Retire <span className="font-semibold">{eq.equipment_name}</span>?
                                    It will no longer appear in the catalog. This is reversible only by an admin.
                                </div>
                                <div className="flex gap-3">
                                    <button
                                        onClick={() => setShowRetireConfirm(false)}
                                        className="px-3 py-1.5 text-sm text-pt-text-muted hover:text-pt-text"
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        onClick={handleRetire}
                                        disabled={deactivateMut.isPending}
                                        className="px-3 py-1.5 text-sm bg-pt-red text-white rounded font-semibold disabled:opacity-50"
                                    >
                                        {deactivateMut.isPending ? 'Retiring…' : 'Confirm retire'}
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                )}
            </main>
        </div>
    );
}

// ----------------------------------------------------------------------------
// Helper: maintenance date display with overdue/soon coloring
// ----------------------------------------------------------------------------

function NextMaintenanceDisplay({ date }: { date: string | null }) {
    if (!date) return <EmptyValue />;
    const due = new Date(date);
    const now = new Date();
    const diffDays = Math.floor((due.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
    const formatted = formatDate(date);

    if (diffDays < 0) {
        return (
            <span className="text-pt-red">
                {formatted}{' '}
                <span className="text-xs">
                    ({Math.abs(diffDays)} day{Math.abs(diffDays) === 1 ? '' : 's'} overdue)
                </span>
            </span>
        );
    }
    if (diffDays <= 7) {
        return (
            <span className="text-pt-amber">
                {formatted}{' '}
                <span className="text-xs">(in {diffDays} day{diffDays === 1 ? '' : 's'})</span>
            </span>
        );
    }
    return formatted;
}
